import { ShieldCheckIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ChatUiVoucherCancellation } from '@voll-intelligence/genui-contract';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { BLOCK_SHELL } from '../../../shared/ui/block_shell.js';
import { withBold } from '../../../shared/ui/bold_markup.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import '../../../shared/ui/flight_ui.css';

/** Uma linha do resumo: a chave apagada numa coluna de 120 e o valor ao lado, com o fio
 *  de 1 px embaixo. O código do voucher vai em mono. */
function SummaryRow({
  label,
  value,
  mono = false,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex gap-3 border-b border-border py-2 text-detail">
      <dt className="w-30 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={`m-0 min-w-0 flex-1 text-foreground ${mono ? 'font-mono' : 'font-medium'}`}>
        {value}
      </dd>
    </div>
  );
}

type Summary = NonNullable<ChatUiVoucherCancellation['summary']>;

/** A corrida (#6699) tem copy e ordem próprias; o modal chega como rótulo livre. */
const RIDE_MODALS = new Set(['CORRIDA', 'MOBILITY']);
function isRide(modal: string | undefined): boolean {
  return modal !== undefined && RIDE_MODALS.has(modal.toUpperCase());
}

/** "Trecho" só quando é uma rota (origem → destino, ex.: voo); modais de estadia
 *  (hotel) trazem um lugar só → "Local". */
function RouteRow({ route }: { readonly route: string }) {
  const { t } = useGenUiI18n();
  return (
    <SummaryRow
      label={route.includes(' → ') ? t('voucherCancel.k.route') : t('voucherCancel.k.place')}
      value={route}
    />
  );
}

function VoucherSummary({ summary: s }: { readonly summary: Summary }) {
  const { t } = useGenUiI18n();
  return (
    <dl className="m-0">
      <SummaryRow label={t('voucherCancel.k.voucher')} value={s.voucherCode} mono />
      {s.modal && <SummaryRow label={t('voucherCancel.k.modal')} value={s.modal} />}
      {s.route && <RouteRow route={s.route} />}
      {s.status && <SummaryRow label={t('voucherCancel.k.status')} value={s.status} />}
      {s.reason && <SummaryRow label={t('voucherCancel.k.reason')} value={s.reason} />}
    </dl>
  );
}

/** Corrida (#6699): o trecho no topo — sempre «Trecho», qualquer que seja o
 *  separador — e o voucher por último. Confirmado, o «Status» vira o «Horário»
 *  da corrida (quando o produtor o manda). */
function RideSummary({ summary: s, sent }: { readonly summary: Summary; readonly sent: boolean }) {
  const { t } = useGenUiI18n();
  const showWhen = sent && s.when !== undefined;
  return (
    <dl className="m-0">
      {s.route && <SummaryRow label={t('voucherCancel.k.route')} value={s.route} />}
      {showWhen ? (
        <SummaryRow label={t('voucherCancel.k.when')} value={s.when} />
      ) : (
        s.status && <SummaryRow label={t('voucherCancel.k.status')} value={s.status} />
      )}
      {s.modal && <SummaryRow label={t('voucherCancel.k.modal')} value={s.modal} />}
      <SummaryRow label={t('voucherCancel.k.voucher')} value={s.voucherCode} mono />
      {s.reason && <SummaryRow label={t('voucherCancel.k.reason')} value={s.reason} />}
    </dl>
  );
}

function RideTitle() {
  const { t } = useGenUiI18n();
  return <h3 className="m-0 text-title text-foreground">{t('voucherCancel.ride.title')}</h3>;
}

/** O fecho ainda aberto: «Manter» outline (só com quick-reply) e o destrutivo. */
function CancelActions({
  ride,
  onKeep,
  onConfirm,
}: {
  readonly ride: boolean;
  readonly onKeep: ((text: string) => void) | undefined;
  readonly onConfirm: (() => void) | undefined;
}) {
  const { t } = useGenUiI18n();
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {onKeep && (
        <Button
          variant="outline"
          onClick={() => {
            onKeep(t('voucherCancel.replyKeep'));
          }}
        >
          {t(ride ? 'voucherCancel.ride.keep' : 'voucherCancel.keep')}
        </Button>
      )}
      <Button variant="destructive" disabled={!onConfirm} onClick={onConfirm}>
        {t('voucherCancel.confirm')}
      </Button>
    </div>
  );
}

/**
 * Card de confirmação de cancelamento de voucher (`voucher_cancellation`) — o
 * guardrail de ação destrutiva: o clique ecoa o `challengeId` ESTRUTURADO via
 * `onConfirm` (nunca texto interpretado pelo LLM); o backend verifica o challenge
 * server-side antes de cancelar. Resumo display-only + CTA, congela com
 * `readOnly`. Quando `summary.postEmission`, avisa que o voucher emitido vira uma
 * SOLICITAÇÃO de cancelamento (revisão manual + marca `não utilizado`).
 *
 * Na casca do bloco (#6325, Figma H4.7): a fala do agente fica fora, acima; o
 * «Confirmar cancelamento» é o destrutivo do DS (nunca terracota) e o «Manter
 * voucher», outline. Confirmado, o fecho vira o registro «Confirmação enviada»,
 * sem botão nenhum — e o foco, que estava no botão que sumiu, passa para ele.
 *
 * Corrida (#6699): título «Cancelar esta corrida?», ordem Trecho · Status · Modal
 * · Voucher, «Manter corrida» e o registro «Cancelamento solicitado».
 */
export function VoucherCancellationCard({
  confirmation,
  note,
  readOnly = false,
  onConfirm,
  onQuickReply,
}: {
  readonly confirmation: ChatUiVoucherCancellation;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly onConfirm?: (confirmation: ChatUiVoucherCancellation) => void;
  readonly onQuickReply?: (text: string) => void;
}) {
  // Trava local anti duplo-clique (a trava real — single-use — é server-side).
  const [sent, setSent] = useState(false);
  const record = useRef<HTMLParagraphElement>(null);
  const { t } = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();
  const s = confirmation.summary;
  const postEmission = s?.postEmission === true;
  const ride = isRide(s?.modal);
  useEffect(() => {
    if (sent) record.current?.focus();
  }, [sent]);
  const confirm = () => {
    setSent(true);
    onConfirm?.(confirmation);
  };
  return (
    <section className="fui" aria-label={t('voucherCancel.aria')}>
      {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
      <div className={BLOCK_SHELL}>
        {ride && !sent && <RideTitle />}
        {postEmission && (
          <p className="m-0 text-detail text-foreground">
            {withBold(t('voucherCancel.leadPostEmission'))}
          </p>
        )}
        {s && (ride ? <RideSummary summary={s} sent={sent} /> : <VoucherSummary summary={s} />)}
        {sent ? (
          <p
            ref={record}
            tabIndex={-1}
            className="m-0 flex items-center gap-2 text-ui text-muted-foreground outline-none"
          >
            <ShieldCheckIcon size={16} className="flex-none" aria-hidden />
            {t(ride ? 'voucherCancel.ride.sent' : 'voucherCancel.sent')}
          </p>
        ) : (
          !readOnly && (
            <CancelActions ride={ride} onKeep={onQuickReply} onConfirm={onConfirm && confirm} />
          )
        )}
      </div>
    </section>
  );
}
